// secrets_view — PURE helpers for the owner's Secrets panel. Kept out of the React
// component so the real logic (client-side value validation + the display ordering) is
// unit-tested directly, matching this codebase's hooks/lib test convention (no rendering).

import type { SecretStatus } from "../api";

// Mirror the deployer's own guard (unit-bundle/app/deployer.ts /secrets/set) EXACTLY, so
// the panel fails fast with the same rule the server enforces — never stricter, never looser.
export const MAX_SECRET_VALUE_LEN = 8192;

export type ValueCheck = { ok: true; value: string } | { ok: false; error: string };

// Validate a pasted secret value. Crucially it does NOT trim or otherwise mutate the value —
// a secret is sent to the server BYTE-FOR-BYTE as typed (trimming could silently corrupt a
// key/token). We only reject the two things the server rejects: empty and over-length.
export function validateSecretValue(raw: string): ValueCheck {
  if (raw.length === 0) return { ok: false, error: "Enter a value." };
  if (raw.length > MAX_SECRET_VALUE_LEN) return { ok: false, error: `Value is too long (max ${MAX_SECRET_VALUE_LEN} characters).` };
  return { ok: true, value: raw };
}

// Display order: UNSET secrets first (they need the operator's attention), then by name.
// Pure + non-mutating (returns a new array) so it's safe to call in render.
export function sortSecrets(list: readonly SecretStatus[]): SecretStatus[] {
  return [...list].sort((a, b) => {
    if (a.isSet !== b.isSet) return a.isSet ? 1 : -1; // unset (false) sorts first
    return a.name.localeCompare(b.name);
  });
}

// One-line status for a secret row. Kept pure so the wording is locked by a test.
export function secretStatusLabel(s: Pick<SecretStatus, "isSet">): "Set" | "Not set" {
  return s.isSet ? "Set" : "Not set";
}
